import { Component, ElementRef, HostListener, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  HOW_RESULTS_LABELS, HowResults, RosterRef, WHO_PAYS_LABELS, WhoPays,
} from '../../models/invitation.model';
import { InvitationMockService } from '../../services/invitation-mock.service';

@Component({
  selector: 'app-invitation-step-details',
  templateUrl: './step-details.component.html',
  styleUrls: ['./step-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationStepDetailsComponent {
  @Input() form!: FormGroup;
  @Input() rosters: RosterRef[] = [];
  @Input() keysShortfall = 0;
  @Input() showStatus = false;

  /**
   * Render results-delivery and payment read-only (the edit modal). They're commitments made to
   * participants at creation — someone may already have paid, or been told how they'd get results.
   *
   * Deliberately not `control.disable()`: reactive forms drop disabled controls from `form.value`,
   * and the edit save reads `v.whoPays` / `v.howResults` straight off it, so disabling would write
   * `undefined` over both. Keeping the controls enabled and hiding the inputs preserves the values.
   */
  @Input() lockSetup = false;

  get howResultsLabel(): string { return HOW_RESULTS_LABELS[this.form.value.howResults as HowResults] ?? '—'; }
  get whoPaysLabel(): string { return WHO_PAYS_LABELS[this.form.value.whoPays as WhoPays] ?? '—'; }

  @ViewChild('newOrgInput') newOrgInput?: ElementRef<HTMLInputElement>;
  @ViewChild('orgCombo') orgCombo?: ElementRef<HTMLElement>;
  @ViewChild('orgSearch') orgSearch?: ElementRef<HTMLInputElement>;

  /** Searchable organization dropdown state. */
  orgOpen = false;
  orgFilter = '';
  orgActiveIndex = 0;

  /** Inline "create organization" panel state. */
  creatingOrg = false;
  newOrgName = '';
  newOrgError = '';

  constructor(private svc: InvitationMockService) {}

  /** Show a field's validation message once it's been touched (or a Next attempt marked it). */
  showError(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && c.touched;
  }

  // --- searchable organization dropdown ---

  get selectedRosterName(): string {
    const id = this.form.get('rosterId')!.value;
    return this.rosters.find(r => r.id === id)?.name ?? '';
  }

  get filteredRosters(): RosterRef[] {
    const q = this.orgFilter.trim().toLowerCase();
    if (!q) { return this.rosters; }
    return this.rosters.filter(r => r.name.toLowerCase().includes(q));
  }

  openOrgMenu(): void {
    if (this.creatingOrg) { return; }
    this.orgOpen = true;
    this.orgFilter = '';
    this.orgActiveIndex = Math.max(0, this.filteredRosters.findIndex(r => r.id === this.form.get('rosterId')!.value));
    setTimeout(() => this.orgSearch?.nativeElement.focus());
  }

  closeOrgMenu(): void {
    this.orgOpen = false;
    this.form.get('rosterId')!.markAsTouched();
  }

  onOrgFilter(value: string): void {
    this.orgFilter = value;
    this.orgActiveIndex = 0;
  }

  selectRoster(r: RosterRef): void {
    this.form.get('rosterId')!.setValue(r.id);
    this.form.get('rosterId')!.markAsTouched();
    this.orgOpen = false;
    this.orgFilter = '';
  }

  /** Keyboard navigation within the open menu. */
  onOrgKeydown(event: KeyboardEvent): void {
    if (!this.orgOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      this.openOrgMenu();
      event.preventDefault();
      return;
    }
    if (!this.orgOpen) { return; }
    const list = this.filteredRosters;
    switch (event.key) {
      case 'ArrowDown':
        this.orgActiveIndex = Math.min(this.orgActiveIndex + 1, list.length - 1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.orgActiveIndex = Math.max(this.orgActiveIndex - 1, 0);
        event.preventDefault();
        break;
      case 'Enter':
        if (list[this.orgActiveIndex]) { this.selectRoster(list[this.orgActiveIndex]); }
        event.preventDefault();
        break;
      case 'Escape':
        this.closeOrgMenu();
        event.preventDefault();
        break;
    }
  }

  /** Close the menu when clicking anywhere outside the combobox. */
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (this.orgOpen && this.orgCombo && !this.orgCombo.nativeElement.contains(target)) {
      this.closeOrgMenu();
    }
  }

  // --- inline create organization ---

  startCreateOrg(): void {
    this.orgOpen = false;
    this.creatingOrg = true;
    this.newOrgName = this.orgFilter.trim(); // carry over whatever was typed in the search
    this.newOrgError = '';
    setTimeout(() => this.newOrgInput?.nativeElement.focus());
  }

  cancelCreateOrg(): void {
    this.creatingOrg = false;
    this.newOrgName = '';
    this.newOrgError = '';
  }

  /** Create the org, add it to the shared roster list, and select it. */
  confirmCreateOrg(): void {
    const name = this.newOrgName.trim();
    if (!name) { this.newOrgError = 'Enter an organization name.'; return; }
    if (this.rosters.some(r => r.name.toLowerCase() === name.toLowerCase())) {
      this.newOrgError = 'An organization with this name already exists.';
      return;
    }
    this.svc.addRoster(name).subscribe(roster => {
      // The input array is shared with the host, so pushing here updates its options too.
      this.rosters.push(roster);
      this.selectRoster(roster);
      this.cancelCreateOrg();
    });
  }
}
